import { ChildrenTypography, TestCardContainer } from '@Src/_basic/components/MuiStyleComponent/TestComponent';
import { checkIsNaN } from '@Src/_basic/helpers/CheckIsNaN';
import { MonthlyReportFlyAshLossOfIgnition, ReportViewResponse } from '@Src/_basic/object/MonthlyReportType';
import { Stack } from '@mui/material';
import moment from 'moment';
import React, { memo, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ReportBlock from './components/ReportBlock';
import RowContent from './components/RowContent';
import ToggleBlockButton from './components/ToggleBlockButton';

type FilterType = 'pieces' | 'maxmin' | null;

const FlyAshLossOfIgnitionReport = ({
  report,
  headerData,
}: {
  report: Array<MonthlyReportFlyAshLossOfIgnition>;
  headerData: ReportViewResponse;
}) => {
  const { t: i18T } = useTranslation();
  const [summary, setSummery] = useState({ max: 0, min: 0, average: 0 });
  const [abnormalSummary, setAbnormalSummery] = useState({
    pieces: 0,
    days: 0,
    dateRecord: {},
  });
  const [selectedFilter, setSelectedFilter] = useState<FilterType>(null);

  const [reports, setReports] = useState<Array<MonthlyReportFlyAshLossOfIgnition>>([]);
  const [filteredReport, setFilteredReport] = useState<Array<MonthlyReportFlyAshLossOfIgnition>>([]);

  const handleFilterChange = (filter: FilterType) => () => {
    const newFilter = selectedFilter === filter ? null : filter;
    const filteredReport = reports.filter((d: MonthlyReportFlyAshLossOfIgnition) => {
      switch (newFilter) {
        case 'pieces':
          return d.weight.isAbnormal;
        case 'maxmin':
          return d.weight.lossRatio === summary.min || d.weight.lossRatio === summary.max;
        default:
          return true;
      }
    });
    setSelectedFilter(newFilter);
    setFilteredReport(filteredReport);
  };

  useEffect(() => {
    const dateRecord: any = {};
    const summary = { max: Number.MIN_VALUE, min: report.length > 0 ? Number.MAX_VALUE : 0, average: 0, sum: 0 };

    let pieces = 0;
    report.forEach((d: MonthlyReportFlyAshLossOfIgnition) => {
      const { lossRatio } = d.weight;
      summary.max = Math.max(summary.max, lossRatio);
      summary.min = Math.min(summary.min, lossRatio);
      summary.sum += lossRatio;

      if (d.weight.isAbnormal) {
        const date = moment(d.testDate).format('YYYY/MM/DD');
        if (!dateRecord[date]) dateRecord[date] = 1;
        pieces += 1;
      }
    });

    setReports(report);
    setFilteredReport(report);
    setSummery({
      max: summary.max,
      min: summary.min,
      average: checkIsNaN(summary.sum / report.length),
    });
    setAbnormalSummery({
      pieces,
      days: Object.keys(dateRecord).length,
      dateRecord,
    });
  }, [report]);

  return (
    <>
      <TestCardContainer sx={{ flexDirection: 'row', gap: 3, justifyContent: 'space-between' }}>
        <ToggleBlockButton
          top={
            <>
              <ChildrenTypography>{`${
                new Date(headerData?.endTime).getDate() - new Date(headerData?.startTime).getDate() + 1
              }
              ${i18T(`MONTHLY_REPORT.BASE.DAY`)}${i18T(`MONTHLY_REPORT.LOSS_ON_IGNITION.LOSS_ON_IGNITION`)}
              ${i18T(`MONTHLY_REPORT.BASE.AVERAGE`)}`}</ChildrenTypography>
              <ChildrenTypography>{summary.average?.toFixed(1)}</ChildrenTypography>
            </>
          }
          bottom={
            <Stack pt={1}>
              <ChildrenTypography>{i18T(`MONTHLY_REPORT.LOSS_ON_IGNITION.CONTRACT_SPECIFICATION`)}</ChildrenTypography>
              <ChildrenTypography>≥40</ChildrenTypography>
            </Stack>
          }
        />
        <ToggleBlockButton
          isToggled={selectedFilter === 'pieces'}
          onPress={handleFilterChange('pieces')}
          top={
            <>
              <ChildrenTypography>{`${
                new Date(headerData?.endTime).getDate() - new Date(headerData?.startTime).getDate() + 1
              }
              ${i18T(`MONTHLY_REPORT.BASE.DAY`)}L${i18T(`MONTHLY_REPORT.LOSS_ON_IGNITION.LOSS_ON_IGNITION_VALUE`)}
              ${i18T(`MONTHLY_REPORT.BASE.AVERAGE`)}`}</ChildrenTypography>
              <ChildrenTypography>{`${i18T(`MONTHLY_REPORT.BASE.ABNORMAL`)}${i18T(
                `MONTHLY_REPORT.BASE.ITEMS`,
              )}`}</ChildrenTypography>
            </>
          }
          bottom={
            <Stack flexDirection="row" mt={3}>
              <ChildrenTypography style={abnormalSummary.days ? { color: `#FFA0F0` } : {}}>
                {abnormalSummary.pieces}
              </ChildrenTypography>
              <ChildrenTypography>{i18T(`MONTHLY_REPORT.BASE.ITEM`)}</ChildrenTypography>
            </Stack>
          }
        />
        <ToggleBlockButton
          isToggled={selectedFilter === 'maxmin'}
          onPress={handleFilterChange('maxmin')}
          top={
            <>
              <ChildrenTypography>{i18T(`MONTHLY_REPORT.LOSS_ON_IGNITION.MAX_LOSS_ON_IGNITION`)}</ChildrenTypography>
              <ChildrenTypography>{summary.max?.toFixed(2)}</ChildrenTypography>
            </>
          }
          bottom={
            <Stack pt={1}>
              <ChildrenTypography>{i18T(`MONTHLY_REPORT.LOSS_ON_IGNITION.MIN_LOSS_ON_IGNITION`)}</ChildrenTypography>
              <ChildrenTypography>{summary.min?.toFixed(2)}</ChildrenTypography>
            </Stack>
          }
        />
      </TestCardContainer>
      {filteredReport &&
        filteredReport?.map((item: MonthlyReportFlyAshLossOfIgnition) => (
          <ReportBlock
            key={item.exptId}
            tester={item.tester}
            exptId={item.exptId}
            exptItemCode={item.exptItemCode}
            verifyPassedResults={item.verifyPassedResults}
            verifyCustomResults={item.verifyCustomResults}
          >
            <RowContent
              widths={[35, 35, 35]}
              labels={[
                i18T(`MONTHLY_REPORT.BASE.TEST_DATE`),
                i18T(`MONTHLY_REPORT.BASE.SAMPLING_DATE`),
                `${i18T(`MONTHLY_REPORT.LOSS_ON_IGNITION.BEFORE_LOSS_ON_IGNITION_WEIGHT`)}(g)`,
              ]}
              values={[
                moment(item.testDate).format('YYYY/MM/DD'),
                moment(item.sampleDate).format('YYYY/MM/DD'),
                item.weight.original?.toFixed(2),
              ]}
            />
            <RowContent
              widths={[35, 35, 35]}
              labels={[
                `${i18T(`MONTHLY_REPORT.LOSS_ON_IGNITION.AFTER_LOSS_ON_IGNITION_WEIGHT`)}(g)`,
                `${i18T(`MONTHLY_REPORT.LOSS_ON_IGNITION.LOSS_WEIGHT`)}(g)`,
                `${i18T(`MONTHLY_REPORT.LOSS_ON_IGNITION.LOSS_ON_IGNITION`)}(%)`,
              ]}
              values={[
                (item.weight?.original - item.weight?.loss)?.toFixed(2),
                item.weight?.loss?.toFixed(2) ?? '0',
                [item.weight?.lossRatio?.toFixed(2) ?? '0', item.weight?.isAbnormal],
              ]}
            />
          </ReportBlock>
        ))}
    </>
  );
};

export default memo(FlyAshLossOfIgnitionReport);
